import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { observer, MobXProviderContext } from 'mobx-react';
import { isEqual, isNumber } from 'lodash';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { themeGet } from 'styled-system';
import { FiSettings, FiSearch, FiClock, FiDownload } from 'react-icons/fi';

import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  Switch,
  Tag,
  Text,
  EmptyState
} from 'core/components';
import { getStartAndEndFromLookback, timezone } from 'core/util/dateUtils';
import QueryModel from 'app/stores/ude/QueryModel';
import { Query, MeasurementName, Filters } from '@kentik/ui-shared/query/query.types';
import { getMsValue, healthToIntent, healthToIcon } from 'app/views/synthetics/utils/syntheticsUtils';
import { formatHealth } from 'app/views/ude/dataviews/util/unitFormatter';
import { arrayToCsv } from 'app/util/arrayToCsv';
import LabelList from 'app/components/labels/LabelList';

// --- Typed wrappers for JS imports ---

type HealthInput = { health: string; isAggregated?: boolean };
type IntentValue = 'none' | 'success' | 'warning' | 'danger';

/** Typed wrapper for healthToIntent (untyped JS util) */
const getHealthIntent = (healthInput: HealthInput): IntentValue =>
  (healthToIntent as (h: HealthInput) => IntentValue)(healthInput);

/** Typed wrapper for healthToIcon (untyped JS util) */
const getHealthIcon = (healthInput: HealthInput): string | null =>
  (healthToIcon as (h: HealthInput) => string | null)(healthInput);

/** MobX model label — supports both plain objects and MobX models with get() */
interface SynthLabel {
  id?: string;
  name?: string;
  get?: (key: string) => string | undefined;
}

/** Typed LabelList — untyped JS component */
const TypedLabelList = LabelList as unknown as React.ComponentType<{
  labels: SynthLabel[];
  maxLabelsToShow?: number;
}>;

// --- Constants & Types ---

type RollupFn = 'min' | 'avg' | 'max';

interface MetricDef {
  name: string;
  label: string;
  unit: 'duration:microseconds' | 'percent';
  healthMetric?: string;
}

interface HealthMetricDef {
  name: string;
}

interface MeasurementMetrics {
  measurement: MeasurementName;
  metrics: MetricDef[];
  healthMetrics: HealthMetricDef[];
}

const TEST_TYPE_TO_MEASUREMENT: Record<string, MeasurementName> = {
  agent: '/synthetics/ping',
  isFlowBased: '/synthetics/ping',
  isHostname: '/synthetics/ping',
  isBulkIpTest: '/synthetics/ping',
  isMesh: '/synthetics/ping',
  isDns: '/synthetics/dns',
  url: '/synthetics/http',
  'page-load': '/synthetics/pageload'
};

const MEASUREMENT_METRICS: Record<string, MeasurementMetrics> = {
  '/synthetics/ping': {
    measurement: '/synthetics/ping',
    metrics: [
      {
        name: 'ping_latency_avg',
        label: 'Latency',
        unit: 'duration:microseconds',
        healthMetric: 'ping_latency_health'
      },
      { name: 'ping_jitter_avg', label: 'Jitter', unit: 'duration:microseconds', healthMetric: 'ping_jitter_health' },
      { name: 'ping_packet_loss_pct', label: 'Packet Loss', unit: 'percent', healthMetric: 'ping_packet_loss_health' }
    ],
    healthMetrics: [
      { name: 'ping_latency_health' },
      { name: 'ping_jitter_health' },
      { name: 'ping_packet_loss_health' }
    ]
  },
  '/synthetics/dns': {
    measurement: '/synthetics/dns',
    metrics: [
      {
        name: 'dns_latency_avg',
        label: 'DNS Latency',
        unit: 'duration:microseconds',
        healthMetric: 'dns_latency_health'
      }
    ],
    healthMetrics: [{ name: 'dns_latency_health' }]
  },
  '/synthetics/http': {
    measurement: '/synthetics/http',
    metrics: [
      { name: 'http_latency_avg', label: 'Latency', unit: 'duration:microseconds', healthMetric: 'http_latency_health' }
    ],
    healthMetrics: [{ name: 'http_latency_health' }]
  },
  '/synthetics/pageload': {
    measurement: '/synthetics/pageload',
    metrics: [
      {
        name: 'pageload_latency_avg',
        label: 'Latency',
        unit: 'duration:microseconds',
        healthMetric: 'pageload_latency_health'
      }
    ],
    healthMetrics: [{ name: 'pageload_latency_health' }]
  }
};

const QUERY_SETTINGS = {
  chart: {
    enable: false,
    type: 'table' as const,
    colors: 'default' as const,
    legend: { enable: false, position: 'right' as const, mode: 'condensed' as const, showRollups: true },
    xAxis: { timezone: 'utc' as const },
    yAxis: { type: 'linear' as const }
  }
};

// --- Styled Components ---

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const StyledTh = styled.th<{ align?: string }>`
  text-align: ${({ align }) => align || 'left'};
  padding: 8px 10px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${themeGet('colors.muted')};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  white-space: nowrap;
`;

const StyledTd = styled.td<{ align?: string }>`
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  vertical-align: middle;
  text-align: ${({ align }) => align || 'left'};
`;

const StyledTr = styled.tr`
  &:hover {
    background: ${themeGet('colors.primaryBackground')};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid ${themeGet('colors.border')};
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  background: ${themeGet('colors.appBackground')};
  color: ${themeGet('colors.body')};

  &::placeholder {
    color: ${themeGet('colors.muted')};
  }

  &:focus {
    border-color: ${themeGet('colors.primary')};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const HealthDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color};
  margin-right: 6px;
`;

const DataViewToggle = styled.button<{ active?: boolean }>`
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  color: ${themeGet('colors.body')};
  border-bottom: 2px solid transparent;

  ${({ active }) =>
    active &&
    css`
      color: ${themeGet('colors.primary')};
      border-bottom-color: ${themeGet('colors.primary')};
    `}

  &:hover {
    color: ${themeGet('colors.primary')};
  }
`;

const SummaryTab = styled.button<{ active?: boolean }>`
  padding: 8px 20px;
  text-align: center;
  background: none;
  border: none;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;

  ${({ active }) =>
    active &&
    css`
      border-bottom-color: ${themeGet('colors.primary')};
    `}

  &:hover {
    border-bottom-color: ${themeGet('colors.primaryLight', 'colors.primary')};
  }
`;

// --- Query Builder ---

function buildTestListQuery({
  measurement,
  measurementMetrics,
  labelIds,
  rollupFn,
  lookbackSeconds,
  startTime,
  endTime
}: {
  measurement: MeasurementName;
  measurementMetrics: MeasurementMetrics;
  labelIds?: string[];
  rollupFn: RollupFn;
  lookbackSeconds: number;
  startTime: number;
  endTime: number;
}): Query {
  const metrics = [
    ...measurementMetrics.metrics.map((m) => ({ name: m.name })),
    ...measurementMetrics.healthMetrics.map((m) => ({ name: m.name }))
  ];

  const rollups = [
    ...measurementMetrics.metrics.map((m) => ({
      metric: m.name,
      fn: rollupFn
    })),
    ...measurementMetrics.healthMetrics.map((m) => ({
      metric: m.name,
      fn: 'min' as const
    }))
  ];

  const filters: Query['filters'] = {};
  if (labelIds && labelIds.length > 0) {
    const dimensionFilters: Filters = {
      connector: 'All',
      filterGroups: [
        {
          connector: 'Any',
          not: false,
          filters: labelIds.map((id) => ({
            filterField: 'ktlabel_syn_test',
            operator: '=',
            filterValue: String(id)
          }))
        }
      ]
    };
    filters.dimensions = dimensionFilters;
  }

  return {
    measurement,
    dimensions: [{ name: 'test_id' }],
    metrics,
    filters,
    rollups,
    time: {
      lookback: lookbackSeconds,
      start: startTime,
      end: endTime
    },
    viz: { type: 'table', limit: 1000 },
    limit: 1000
  };
}

// --- Time Range Helper ---

function getTimeRange(query: { lookback_seconds: number; starting_time?: number; ending_time?: number }): {
  lookbackSeconds: number;
  startTime: number;
  endTime: number;
} {
  const { lookback_seconds, starting_time, ending_time } = query;

  if (starting_time != null && ending_time != null) {
    return {
      lookbackSeconds: 0,
      startTime: isNumber(starting_time) ? starting_time : moment.utc(starting_time).unix(),
      endTime: isNumber(ending_time) ? ending_time : moment.utc(ending_time).unix()
    };
  }

  if (lookback_seconds === 1) {
    const start = timezone.momentFn().date(1).hour(0).minute(0).second(0).millisecond(0);
    const end = timezone.momentFn();
    return {
      lookbackSeconds: 0,
      startTime: moment.utc(start).unix(),
      endTime: moment.utc(end).unix()
    };
  }

  if (lookback_seconds === 2) {
    const start = timezone.momentFn().date(1).hour(0).minute(0).second(0).millisecond(0).subtract(1, 'month');
    const end = timezone.momentFn(start).endOf('month');
    return {
      lookbackSeconds: 0,
      startTime: moment.utc(start).unix(),
      endTime: moment.utc(end).unix()
    };
  }

  if (lookback_seconds > 2) {
    const { start, end } = getStartAndEndFromLookback(lookback_seconds, 60);
    return {
      lookbackSeconds: 0,
      startTime: isNumber(start) ? start : moment.utc(start).unix(),
      endTime: isNumber(end) ? end : moment.utc(end).unix()
    };
  }

  return { lookbackSeconds: 3600, startTime: 0, endTime: 0 };
}

// --- Health Helpers ---

/** Map a UDE health code (100/200/300) to a lowercase label using formatHealth from unitFormatter */
function healthCodeToLabel(value: number): string {
  return formatHealth(value).display.toLowerCase();
}

function deriveHealth(
  rollups: Record<string, number>,
  healthMetrics: HealthMetricDef[]
): { label: string; intent: IntentValue } {
  const healthValues = healthMetrics
    .map((m) => rollups[`min_${m.name}`])
    .filter((v): v is number => v != null && v > 0);

  const worstHealth = healthValues.length > 0 ? Math.min(...healthValues) : null;
  const healthLabel = worstHealth !== null ? healthCodeToLabel(worstHealth) : 'pending';

  return {
    label: healthLabel,
    intent: getHealthIntent({ health: healthLabel })
  };
}

function deriveMetricHealth(rollups: Record<string, number>, healthMetric?: string): string | null {
  if (!healthMetric) {
    return null;
  }
  const val = rollups[`min_${healthMetric}`];
  if (val == null || val <= 0) {
    return null;
  }
  return healthCodeToLabel(val);
}

function formatMetricValue(value: number | undefined | null, unit: string): string {
  if (value == null || Number.isNaN(value)) {
    return '---';
  }
  if (unit === 'percent') {
    // UDE rollup values for percentage metrics are already in 0-100 range
    return `${value === 100 ? '100' : Number(value.toFixed(2))}%`;
  }
  return getMsValue(value);
}

// --- Component ---

/** Minimal interface for a MobX model with .get() accessor — partial type during JS→TS migration */
interface SynthTestModel {
  get: (attr: string) => string | undefined;
  labels: SynthLabel[];
}

/** Partial type for the Collection.get() pattern used by $syn stores */
interface TestCollection {
  get: (id: string) => SynthTestModel | undefined;
}

interface SynthStore {
  tests: TestCollection;
  communityPerformanceTests?: TestCollection;
}

interface DataviewModel {
  query: {
    synth_test_type: string;
    synth_test_id?: string[];
    lookback_seconds: number;
    starting_time?: number;
    ending_time?: number;
  };
  lastRefresh: number;
  updateFrequency: number;
  setFullyLoaded: () => void;
}

interface SynthTestListProps {
  $syn: SynthStore;
  dataview: DataviewModel;
  updateFrequency?: number;
}

const SynthTestListDashboardItem = observer(({ $syn, dataview }: SynthTestListProps) => {
  const { $app, $labels } = useContext(MobXProviderContext);
  const { synth_test_type, synth_test_id, lookback_seconds, starting_time, ending_time } = dataview.query;

  const [rollupFn, setRollupFn] = useState<RollupFn>('avg');
  const [searchFilter, setSearchFilter] = useState('');
  const [healthFilter, setHealthFilter] = useState<string | null>(null);

  const measurement = (
    MEASUREMENT_METRICS[synth_test_type] ? synth_test_type : TEST_TYPE_TO_MEASUREMENT[synth_test_type]
  ) as MeasurementName;
  const measurementMetrics = measurement ? MEASUREMENT_METRICS[measurement] : null;

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = { labels: true, healthIcons: true };
    if (measurementMetrics) {
      measurementMetrics.metrics.forEach((m) => {
        initial[m.name] = true;
      });
    }
    return initial;
  });

  // Re-seed column visibility when measurement changes (e.g. new metric columns)
  useEffect(() => {
    if (!measurementMetrics) {
      return;
    }
    setColumnVisibility((prev) => {
      const updated = { ...prev };
      measurementMetrics.metrics.forEach((m) => {
        if (!(m.name in updated)) {
          updated[m.name] = true;
        }
      });
      return updated;
    });
  }, [measurement, measurementMetrics]);

  const toggleColumn = useCallback((column: string) => {
    setColumnVisibility((prev) => ({ ...prev, [column]: !prev[column] }));
  }, []);

  // Build the UDE query
  const timeRange = useMemo(
    () => getTimeRange({ lookback_seconds, starting_time, ending_time }),
    [lookback_seconds, starting_time, ending_time]
  );

  const query = useMemo(() => {
    if (!measurement || !measurementMetrics) {
      return null;
    }

    const labelIds = Array.isArray(synth_test_id) ? synth_test_id.map(String) : undefined;

    return buildTestListQuery({
      measurement,
      measurementMetrics,
      labelIds,
      rollupFn,
      lookbackSeconds: timeRange.lookbackSeconds,
      startTime: timeRange.startTime,
      endTime: timeRange.endTime
    });
  }, [measurement, measurementMetrics, synth_test_id, rollupFn, timeRange]);

  // Create QueryModel once
  const [queryModel] = useState(() => {
    const initialQuery =
      query ||
      buildTestListQuery({
        measurement: '/synthetics/ping',
        measurementMetrics: MEASUREMENT_METRICS['/synthetics/ping'],
        rollupFn: 'avg',
        lookbackSeconds: 3600,
        startTime: 0,
        endTime: 0
      });
    return new QueryModel(initialQuery, QUERY_SETTINGS);
  });

  // Run query when it changes
  const prevQueryRef = useRef<Query | null>(null);
  useEffect(() => {
    if (!query) {
      return;
    }

    // Only run if query actually changed
    if (prevQueryRef.current && isEqual(prevQueryRef.current, query)) {
      return;
    }
    prevQueryRef.current = query;

    queryModel.query = query;
    queryModel.run();
  }, [query, queryModel]);

  // Handle manual refresh via lastRefresh
  const lastRefreshRef = useRef(dataview.lastRefresh);
  useEffect(() => {
    if (dataview.lastRefresh !== lastRefreshRef.current) {
      lastRefreshRef.current = dataview.lastRefresh;
      if (queryModel.hasRun) {
        queryModel.run();
      }
    }
  }, [dataview.lastRefresh, queryModel]);

  // Auto-refresh interval
  useEffect(() => {
    if (!dataview.updateFrequency || !query) {
      return undefined;
    }
    const interval = setInterval(() => queryModel.run(), dataview.updateFrequency * 1000);
    return () => clearInterval(interval);
  }, [dataview.updateFrequency, query, queryModel]);

  // Cleanup on unmount
  useEffect(() => () => queryModel.cleanup(), [queryModel]);

  // Signal fully loaded for dashboard export
  useEffect(() => {
    if (!measurement || !measurementMetrics || queryModel.fullyLoaded || queryModel.hasError) {
      dataview.setFullyLoaded();
    }
  }, [dataview, measurement, measurementMetrics, queryModel.fullyLoaded, queryModel.hasError]);

  // Derived data — must be above early returns to preserve hook order
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = queryModel.mainQueryRequest.results?.rows ?? [];
  const isExport = $app?.isExport;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleMetrics = measurementMetrics
    ? measurementMetrics.metrics.filter((m) => columnVisibility[m.name] !== false)
    : [];
  const rollupLabel = rollupFn.charAt(0).toUpperCase() + rollupFn.slice(1);

  // Enrich rows with test model data for filtering and rendering
  const enrichedRows = useMemo(() => {
    if (!measurementMetrics) {
      return [];
    }
    return rows.map((row) => {
      const testId = row.dimensions.test_id;
      const testModel = $syn.tests.get(testId) || $syn.communityPerformanceTests?.get(testId);
      const testName = testModel?.get('display_name') ?? `Test ${testId}`;
      const testLabels = testModel?.labels ?? [];
      const health = deriveHealth(row.rollups, measurementMetrics.healthMetrics);
      return { row, testId, testModel, testName, testLabels, health };
    });
  }, [rows, $syn.tests, $syn.communityPerformanceTests, measurementMetrics]);

  // Filter by search term and health
  const filteredRows = useMemo(() => {
    let filtered = enrichedRows;

    // Health filter
    if (healthFilter) {
      filtered = filtered.filter(({ health }) => health.label === healthFilter);
    }

    // Search filter
    if (searchFilter.trim()) {
      const term = searchFilter.toLowerCase();
      filtered = filtered.filter(({ testName, testLabels }) => {
        if (testName.toLowerCase().includes(term)) {
          return true;
        }
        if (!testLabels || !Array.isArray(testLabels)) {
          return false;
        }
        return testLabels.some((label: SynthLabel) => {
          const name = label?.get?.('name') ?? label?.name ?? '';
          return name.toLowerCase().includes(term);
        });
      });
    }

    return filtered;
  }, [enrichedRows, searchFilter, healthFilter]);

  // Health summary counts
  const healthCounts = useMemo(() => {
    const counts = { total: enrichedRows.length, healthy: 0, critical: 0, warning: 0 };
    enrichedRows.forEach(({ health }) => {
      if (health.label === 'healthy') {
        counts.healthy += 1;
      } else if (health.label === 'critical') {
        counts.critical += 1;
      } else if (health.label === 'warning') {
        counts.warning += 1;
      }
    });
    return counts;
  }, [enrichedRows]);

  // Resolve selected labels for header display
  const selectedLabels = useMemo(() => {
    if (!Array.isArray(synth_test_id) || !$labels) {
      return [];
    }
    return synth_test_id.map((id: string) => $labels.labels.get(String(id))).filter(Boolean);
  }, [synth_test_id, $labels]);

  // Format time range for display
  const timeRangeDisplay = useMemo(() => {
    if (!timeRange.startTime || !timeRange.endTime) {
      return '';
    }
    const fmt = 'YYYY-MM-DD HH:mm';
    const start = moment.unix(timeRange.startTime).format(fmt);
    const end = moment.unix(timeRange.endTime).format(fmt);
    return `${start} - ${end}`;
  }, [timeRange]);

  // CSV export
  const exportCsv = useCallback(() => {
    const columns: string[] = ['Health', 'Test Name'];
    if (columnVisibility.labels !== false) {
      columns.push('Labels');
    }
    visibleMetrics.forEach((m) => {
      columns.push(`${rollupLabel} ${m.label}`);
      if (m.healthMetric) {
        columns.push(`${m.label} Health`);
      }
    });

    const healthValueToLabel = (val: number | undefined | null): string => {
      if (val == null || val <= 0) {
        return 'pending';
      }
      return healthCodeToLabel(val);
    };

    const lines = filteredRows.map(({ testName, testLabels, health, row }) => {
      const cells: string[] = [health.label, testName];
      if (columnVisibility.labels !== false) {
        const labelNames = (testLabels ?? []).map((l: SynthLabel) => l?.get?.('name') ?? l?.name ?? '').join('; ');
        cells.push(labelNames);
      }
      visibleMetrics.forEach((m) => {
        const rollupKey = `${rollupFn}_${m.name}`;
        cells.push(formatMetricValue(row.rollups[rollupKey], m.unit));
        if (m.healthMetric) {
          cells.push(healthValueToLabel(row.rollups[`min_${m.healthMetric}`]));
        }
      });
      return cells;
    });

    const csv = arrayToCsv(lines, columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    const filterSuffix = healthFilter ? `-${healthFilter}` : '';
    a.download = `test-list-${rollupFn}${filterSuffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [filteredRows, visibleMetrics, columnVisibility, rollupFn, rollupLabel, healthFilter]);

  // --- Render ---

  if (!measurement || !measurementMetrics) {
    return (
      <EmptyState
        icon="disable"
        title="Unsupported test type"
        description={`Test type "${synth_test_type}" is not supported for the Test List display.`}
      />
    );
  }

  if (queryModel.isLoading) {
    return (
      <Flex flex={1} alignItems="center" justifyContent="center">
        <Spinner intent="primary" />
      </Flex>
    );
  }

  if (queryModel.hasError) {
    return <EmptyState icon="error" title="Query Error" description="Failed to load test data. Please try again." />;
  }

  if (!queryModel.hasResults) {
    return (
      <EmptyState
        icon="disable"
        title="No results"
        description="No test data found for the selected configuration and time range."
      />
    );
  }

  return (
    <Flex flex={1} flexDirection="column" overflow="hidden">
      {/* Header with time range and selected labels */}
      {(timeRangeDisplay || selectedLabels.length > 0) && (
        <Flex px={2} pt={2} pb={1} alignItems="center" gap={2} flexWrap="wrap">
          {timeRangeDisplay && (
            <Flex alignItems="center" gap={1}>
              <FiClock size={14} color="#9e9e9e" />
              <Text fontSize={12} color="muted">
                {timeRangeDisplay}
              </Text>
            </Flex>
          )}
          {selectedLabels.length > 0 && (
            <Flex alignItems="center" gap={1} flexWrap="wrap">
              <Text
                fontSize={11}
                fontWeight={700}
                color="muted"
                style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                Label:
              </Text>
              {selectedLabels.map((label: SynthLabel) => (
                <Tag
                  key={label.id}
                  minimal
                  round
                  style={{ borderLeft: `3px solid ${label?.get?.('color') || '#999'}` }}
                >
                  {label?.get?.('name') ?? label?.name ?? ''}
                </Tag>
              ))}
            </Flex>
          )}
        </Flex>
      )}

      {/* Search bar */}
      {!isExport && (
        <Box px={2} pt={1} pb={1} position="relative">
          <Box position="relative">
            <SearchIcon>
              <FiSearch size={14} color="#9e9e9e" />
            </SearchIcon>
            <SearchInput
              placeholder="Filter tests by name or labels (e.g. 'P1', 'US-East')..."
              value={searchFilter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchFilter(e.target.value)}
            />
          </Box>
        </Box>
      )}

      {/* Summary filter tabs + Data View controls */}
      <Flex px={2} py={2} alignItems="center" justifyContent="space-between" borderBottom="thin">
        <Flex alignItems="center" gap={0}>
          <SummaryTab active={healthFilter === null} onClick={() => setHealthFilter(null)}>
            <Text
              fontSize={11}
              fontWeight={700}
              color="muted"
              as="div"
              style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Total
            </Text>
            <Text fontSize={22} fontWeight={700} as="div">
              {healthCounts.total}
            </Text>
          </SummaryTab>
          <SummaryTab
            active={healthFilter === 'healthy'}
            onClick={() => setHealthFilter(healthFilter === 'healthy' ? null : 'healthy')}
          >
            <Flex alignItems="center" justifyContent="center" mb="2px">
              <HealthDot color="#43a047" />
              <Text
                fontSize={11}
                fontWeight={700}
                color="muted"
                as="span"
                style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                Healthy
              </Text>
            </Flex>
            <Text fontSize={22} fontWeight={700} as="div" color={healthCounts.healthy > 0 ? 'body' : 'muted'}>
              {healthCounts.healthy}
            </Text>
          </SummaryTab>
          <SummaryTab
            active={healthFilter === 'critical'}
            onClick={() => setHealthFilter(healthFilter === 'critical' ? null : 'critical')}
          >
            <Flex alignItems="center" justifyContent="center" mb="2px">
              <HealthDot color="#e53935" />
              <Text
                fontSize={11}
                fontWeight={700}
                color="muted"
                as="span"
                style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                Critical
              </Text>
            </Flex>
            <Text fontSize={22} fontWeight={700} as="div" color={healthCounts.critical > 0 ? 'danger' : 'muted'}>
              {healthCounts.critical}
            </Text>
          </SummaryTab>
          <SummaryTab
            active={healthFilter === 'warning'}
            onClick={() => setHealthFilter(healthFilter === 'warning' ? null : 'warning')}
          >
            <Flex alignItems="center" justifyContent="center" mb="2px">
              <HealthDot color="#fb8c00" />
              <Text
                fontSize={11}
                fontWeight={700}
                color="muted"
                as="span"
                style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
              >
                Warning
              </Text>
            </Flex>
            <Text fontSize={22} fontWeight={700} as="div" color={healthCounts.warning > 0 ? 'warning' : 'muted'}>
              {healthCounts.warning}
            </Text>
          </SummaryTab>
        </Flex>

        {!isExport && (
          <Flex alignItems="center" gap={2}>
            <Text
              fontSize={11}
              fontWeight={700}
              color="muted"
              mr={1}
              style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Data View
            </Text>
            <Flex>
              {(['min', 'avg', 'max'] as const).map((fn) => (
                <DataViewToggle key={fn} active={rollupFn === fn} onClick={() => setRollupFn(fn)}>
                  {fn.toUpperCase()}
                </DataViewToggle>
              ))}
            </Flex>
            <Popover
              position="bottom-right"
              content={
                <Menu>
                  <MenuItem
                    shouldDismissPopover={false}
                    text="Labels"
                    labelElement={
                      <Switch checked={columnVisibility.labels !== false} onChange={() => toggleColumn('labels')} />
                    }
                  />
                  <MenuItem
                    shouldDismissPopover={false}
                    text="Health Icons"
                    labelElement={
                      <Switch
                        checked={columnVisibility.healthIcons !== false}
                        onChange={() => toggleColumn('healthIcons')}
                      />
                    }
                  />
                  {measurementMetrics.metrics.map((m) => (
                    <MenuItem
                      key={m.name}
                      shouldDismissPopover={false}
                      text={m.label}
                      labelElement={
                        <Switch checked={columnVisibility[m.name] !== false} onChange={() => toggleColumn(m.name)} />
                      }
                    />
                  ))}
                </Menu>
              }
            >
              <Button icon={FiSettings} small minimal />
            </Popover>
            <Button
              icon={FiDownload}
              small
              minimal
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                exportCsv();
              }}
            />
          </Flex>
        )}
      </Flex>

      {/* Table */}
      <Box flex={1} overflow="auto">
        <StyledTable>
          <thead>
            <tr>
              <StyledTh>Test Details</StyledTh>
              {visibleMetrics.map((m) => (
                <StyledTh key={m.name} align="right">
                  {rollupLabel} {m.label}
                </StyledTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(({ row, testId, testName, testLabels, health }) => (
              <StyledTr key={row.id}>
                <StyledTd>
                  <Flex alignItems="flex-start" gap={2}>
                    <Box pt="2px">
                      <Tag minimal intent={health.intent} fontWeight="bold" style={{ textTransform: 'capitalize' }}>
                        {health.label}
                      </Tag>
                    </Box>
                    <Box>
                      <Link to={`/v4/synthetics/tests/${testId}/results`} fontWeight="bold">
                        {testName}
                      </Link>
                      {columnVisibility.labels !== false && testLabels.length > 0 && (
                        <Box mt="2px">
                          <TypedLabelList labels={testLabels} maxLabelsToShow={3} />
                        </Box>
                      )}
                    </Box>
                  </Flex>
                </StyledTd>
                {visibleMetrics.map((m) => {
                  const rollupKey = `${rollupFn}_${m.name}`;
                  const value = row.rollups[rollupKey];
                  const metricHealth = deriveMetricHealth(row.rollups, m.healthMetric);
                  const metricHealthIcon = metricHealth ? getHealthIcon({ health: metricHealth }) : null;
                  const metricHealthIntent = metricHealth ? getHealthIntent({ health: metricHealth }) : null;
                  const showHealthIcon = columnVisibility.healthIcons !== false;
                  return (
                    <StyledTd key={m.name} align="right">
                      <Flex alignItems="center" justifyContent="flex-end" gap={1}>
                        {showHealthIcon && metricHealthIcon && (
                          <Icon icon={metricHealthIcon} iconSize={14} color={metricHealthIntent} />
                        )}
                        <Text
                          fontWeight="bold"
                          style={{
                            fontVariantNumeric: 'tabular-nums',
                            minWidth: '70px',
                            textAlign: 'right',
                            display: 'inline-block'
                          }}
                        >
                          {formatMetricValue(value, m.unit)}
                        </Text>
                      </Flex>
                    </StyledTd>
                  );
                })}
              </StyledTr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <StyledTd colSpan={1 + visibleMetrics.length} align="center">
                  <Text muted py={3} as="div">
                    No tests match your filter.
                  </Text>
                </StyledTd>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </Box>
    </Flex>
  );
});

export default SynthTestListDashboardItem;

// Exported for testing
export {
  buildTestListQuery,
  deriveHealth,
  formatMetricValue,
  getTimeRange,
  TEST_TYPE_TO_MEASUREMENT,
  MEASUREMENT_METRICS
};
export type { RollupFn, MeasurementMetrics };
